import { cached, geoKey } from "../lib/cache";

/**
 * Фотографии места. У оригинала это делал Panoramio — Google закрыл его в 2016,
 * и снимки переехали в Wikimedia Commons: в выдаче по Кишинёву до сих пор лежат
 * файлы с суффиксом «- panoramio».
 *
 * Commons: без ключа, CORS через origin=*, лицензии CC. Атрибуция обязательна,
 * поэтому автор и лицензия едут вместе со снимком.
 */
export interface Photo {
  url: string;
  title: string;
  author: string;
  license: string;
  pageUrl: string;
}

/**
 * Геопоиск отдаёт то, что рядом геотегировано, — а не то, что годится в фон.
 * По центру Кишинёва это оказалось кладбище Святой Троицы: категории
 * «Cemetery», «Crucifixes», «Cultural heritage monuments». Под музыку такое
 * ставить нельзя, поэтому кандидаты оцениваются, а не берутся подряд.
 */
const GOOD = [
  "landscape", "panorama", "skyline", "cityscape", "view of", "park", "garden",
  "river", "lake", "sea", "coast", "beach", "mountain", "forest", "sunset",
  "sunrise", "sky", "street", "square", "boulevard", "bridge", "architecture",
  "aerial", "nature", "пейзаж", "парк", "вид",
];

const BAD = [
  "cemeter", "cimitir", "grave", "tomb", "crucifix", "cross", "memorial",
  "victim", "war", "funeral", "кладбищ", "могил",
  "interior", "indoor", "museum", "exhibit", "portrait", "people", "protest",
  "logo", "coat of arms", "map", "diagram", "plaque", "sign", "poster",
  "document", "scan", "screenshot", "stamp", "banknote", "coin",
];

function score(title: string, cats: string, w: number, h: number): number {
  const hay = `${title} ${cats}`.toLowerCase();
  let s = 0;
  for (const g of GOOD) if (hay.includes(g)) s += 2;
  for (const b of BAD) if (hay.includes(b)) s -= 5;

  // Фон широкий, поэтому портрет не годится, а сверхпанорама обрежется в кашу.
  const ratio = h > 0 ? w / h : 0;
  if (ratio >= 1.25 && ratio <= 2.4) s += 3;
  else if (ratio > 2.4 && ratio <= 3.5) s += 1;
  else if (ratio < 1) s -= 3;
  else if (ratio > 4) s -= 4;

  if (w < 800) s -= 3;
  return s;
}

const strip = (html: string) => html.replace(/<[^>]+>/g, "").trim();

async function load(lat: number, lon: number): Promise<Photo[]> {
  const q = new URLSearchParams({
    action: "query", format: "json", origin: "*",
    generator: "geosearch",
    ggscoord: `${lat}|${lon}`,
    ggsradius: "10000",      // потолок Commons, больше не принимает
    ggslimit: "100",          // берём широкий пул, чтобы было из чего выбирать
    ggsnamespace: "6",
    prop: "imageinfo",
    iiprop: "url|extmetadata|size",
    iiurlwidth: "1600",
  });

  const r = await fetch(`https://commons.wikimedia.org/w/api.php?${q}`);
  if (!r.ok) return [];
  const d = await r.json();
  const pages: Record<string, {
    title?: string;
    imageinfo?: {
      thumburl?: string; url?: string; descriptionurl?: string;
      width?: number; height?: number;
      extmetadata?: Record<string, { value?: string }>;
    }[];
  }> = d?.query?.pages ?? {};

  return Object.values(pages)
    .map((p) => {
      const ii = p.imageinfo?.[0];
      const src = ii?.thumburl || ii?.url;
      if (!src) return null;
      const em = ii?.extmetadata ?? {};
      const title = (p.title ?? "").replace(/^File:/, "").replace(/\.[a-z]+$/i, "");
      const cats = strip(em.Categories?.value ?? "");
      return {
        photo: {
          url: src,
          title,
          author: strip(em.Artist?.value ?? "") || "—",
          license: em.LicenseShortName?.value ?? "CC",
          pageUrl: ii?.descriptionurl ?? "https://commons.wikimedia.org/",
        } as Photo,
        s: score(title, cats, ii?.width ?? 0, ii?.height ?? 0),
      };
    })
    .filter((x): x is { photo: Photo; s: number } => x !== null)
    .filter((x) => x.s > -2)          // явно неподходящее не показываем вовсе
    .sort((a, b) => b.s - a.s)
    .slice(0, 8)
    .map((x) => x.photo);
}

/** Снимки места не меняются — держим их сутки. */
export async function fetchPlacePhotos(lat: number, lon: number): Promise<Photo[]> {
  try {
    return await cached(`photos:${geoKey(lat, lon)}`, 24 * 60 * 60 * 1000, () => load(lat, lon));
  } catch {
    return [];   // фон — украшение, его отсутствие не должно ломать музыку
  }
}
